import * as React from 'react';
import * as ReactDOM from 'react-dom';
import classnames from 'classnames';
import { observer } from 'mobx-react';
import { useLocation } from 'react-router-dom';
import { Building, Check } from 'lucide-react';
import { OrganizationNodeProps } from '@ixicare/resources';
import { useSidebarCollapsed, useSidebarDismiss, useRailTooltip } from '@ixicare/ui';
import { t } from '~/i18n';
import { useStores } from '~store/utils';
import { useSelectedSite } from '~hooks/useSelectedSite';
import { useDutyState } from '~hooks/useDutyState';
import { useOutsideClick } from '~hooks/useOutsideClick';
import { getSiteSelectorMode } from '~config/siteSelectorRoutes';
import { useOrganizations } from '~/modules/sites/hooks/useOrganizations';
import menu from '~styles/dropdownMenu.module.css';
import styles from './SiteSelector.module.css';

interface SiteChoice {
  uuid: string;
  name: string;
}

interface SiteOrgNode {
  id: string;
  name: string;
  sites: SiteChoice[];
  children: SiteOrgNode[];
}

/** Reshapes the organization tree down to sites this user actually has, and
 *  prunes branches that end up empty — otherwise an employee's dropdown would
 *  list the whole customer base as headings with nothing under them. */
const buildSiteTree = (nodes: OrganizationNodeProps[], available: Map<string, string>, claimed: Set<string>) => {
  const result: SiteOrgNode[] = [];
  for (const node of nodes) {
    const children = buildSiteTree(node.children, available, claimed);
    const sites = node.sites.filter((s) => available.has(s.uuid));
    if (sites.length === 0 && children.length === 0) continue;
    for (const s of sites) claimed.add(s.uuid);
    result.push({
      id: node.id,
      name: node.name,
      sites: sites.map((s) => ({ uuid: s.uuid, name: available.get(s.uuid)! })),
      children,
    });
  }
  return result;
};

/** Keeps sites whose name matches, plus any organization still holding one.
 *  Matching an organization keeps everything under it, so typing a customer
 *  name reveals all of their sites. */
const filterSiteTree = (nodes: SiteOrgNode[], needle: string, orgMatched = false): SiteOrgNode[] => {
  const result: SiteOrgNode[] = [];
  for (const node of nodes) {
    const selfMatches = orgMatched || node.name.toLowerCase().includes(needle);
    const children = filterSiteTree(node.children, needle, selfMatches);
    const sites = selfMatches ? node.sites : node.sites.filter((s) => s.name.toLowerCase().includes(needle));
    if (sites.length === 0 && children.length === 0) continue;
    result.push({ ...node, sites, children });
  }
  return result;
};

// Below this a list is faster to scan than to type into, and the input would
// just be chrome. react-select showed one unconditionally; this is the tradeoff
// for owning the control.
const SEARCH_THRESHOLD = 8;

export interface SiteSelectorProps {
  /** `sidebar` sizes the trigger to the nav's site header and portals the menu
   *  out of the nav's scroll container, which would otherwise clip it. */
  variant?: 'topbar' | 'sidebar';
}

/** Which site you're looking at. Pure navigation — it changes the page and
 *  nothing else, which is why it never shows a group: you don't handle a site
 *  you're only viewing. Duty lives on the other side of the bar. */
const SiteSelector: React.FC<SiteSelectorProps> = observer(({ variant = 'topbar' }) => {
  const { alerts, sites } = useStores();
  const [selectedSite, setSelectedSiteUuid] = useSelectedSite();
  const { dutySiteUuid } = useDutyState();
  const { data: organizations } = useOrganizations();
  const location = useLocation();

  const isSidebar = variant === 'sidebar';
  // The nav tells its own slot when it has collapsed to the rail — there the
  // trigger keeps only its mark, and the site name becomes its tooltip.
  const collapsedNav = useSidebarCollapsed();
  const isRail = isSidebar && collapsedNav;
  // Non-null only while the nav is an overlay: switching site from in there
  // should get the drawer out of the way too, same as choosing a nav item.
  const dismissNav = useSidebarDismiss();
  const [open, setOpen] = React.useState(false);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);
  const searchRef = React.useRef<HTMLInputElement>(null);

  const outsideRefs = React.useMemo(() => [wrapperRef, menuRef], []);
  useOutsideClick(outsideRefs, () => setOpen(false), open);

  const railTip = useRailTooltip(selectedSite?.name ?? '', isRail && !open, triggerRef as React.RefObject<HTMLElement>);

  // Land on the search box when there is one — that's the fast path through a
  // long list. ArrowDown from there walks into the options as usual.
  React.useEffect(() => {
    if (!open) return;
    if (searchRef.current) {
      searchRef.current.focus();
      return;
    }
    menuRef.current?.querySelector<HTMLButtonElement>('button[role="option"]')?.focus();
  }, [open]);

  // Portaled menus are detached from the trigger, so they have to be told where
  // it is — and re-told whenever anything moves underneath them.
  const [anchor, setAnchor] = React.useState<{ top: number; left: number } | null>(null);
  React.useEffect(() => {
    if (!open || !isSidebar) return undefined;
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect();
      if (rect) setAnchor({ top: rect.bottom + 6, left: rect.left });
    };
    place();
    window.addEventListener('resize', place);
    // Capture phase: the nav's own scroll container doesn't bubble scroll.
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, isSidebar]);

  // Sites you have but the tree doesn't place fall through to a flat list at the
  // bottom — including every site when the organizations call hasn't landed or
  // isn't available, which degrades to exactly the plain list this used to be.
  const { tree: fullTree, unassigned: fullUnassigned } = React.useMemo(() => {
    const available = new Map(sites.sites.map((s) => [s.uuid, s.name] as const));
    const claimed = new Set<string>();
    const built = buildSiteTree(organizations ?? [], available, claimed);
    return {
      tree: built,
      unassigned: sites.sites.filter((s) => !claimed.has(s.uuid)).map((s) => ({ uuid: s.uuid, name: s.name })),
    };
  }, [organizations, sites.sites]);

  const [query, setQuery] = React.useState('');
  React.useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const { tree, unassigned } = React.useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return { tree: fullTree, unassigned: fullUnassigned };
    return {
      tree: filterSiteTree(fullTree, needle),
      unassigned: fullUnassigned.filter((s) => s.name.toLowerCase().includes(needle)),
    };
  }, [query, fullTree, fullUnassigned]);

  const mode = getSiteSelectorMode(location.pathname);
  // In the bar, a hidden route means no control at all. In the nav it fills the
  // site-header slot, so returning null there would leave an empty row where
  // the site name belongs — it stays, just not as a switcher.
  if (mode === 'hidden' && !isSidebar) return null;
  if (!sites.loaded || sites.sites.length === 0) return null;

  // Nothing to switch to: a single-site HCP still sees which site they're on
  // (useful in the nav) but not a caret promising a choice that isn't there.
  const enabled = mode === 'enabled' && sites.sites.length > 1;

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      close();
      return;
    }
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
    e.preventDefault();
    const items = Array.from(menuRef.current?.querySelectorAll<HTMLButtonElement>('button[role="option"]') ?? []);
    if (items.length === 0) return;
    const idx = items.indexOf(document.activeElement as HTMLButtonElement);
    const next =
      e.key === 'ArrowDown' ? items[(idx + 1) % items.length] : items[(idx - 1 + items.length) % items.length];
    next?.focus();
  };

  const renderSite = (site: SiteChoice) => {
    // Only the duty site is annotated: every site tends to have open alarms, so
    // decorating all of them is noise — what matters is the one you're on the
    // hook for while looking elsewhere.
    const openCount = site.uuid === dutySiteUuid ? alerts.openCountFor(site.uuid) : 0;
    const isSelected = site.uuid === selectedSite?.uuid;
    return (
      <li key={site.uuid}>
        <button
          type="button"
          role="option"
          aria-selected={isSelected}
          className={menu.MenuItem}
          onClick={() => {
            close();
            setSelectedSiteUuid(site.uuid);
            dismissNav?.();
          }}
        >
          <span className={styles.RowName}>{site.name}</span>
          <span className={styles.Trailing}>
            {openCount > 0 && <span className={styles.RowCount}>{t('alertMode.openShort', { count: openCount })}</span>}
            {isSelected && (
              <span className={menu.Check} aria-hidden="true">
                <Check size={14} />
              </span>
            )}
          </span>
        </button>
      </li>
    );
  };

  // The popover wraps the listbox rather than being it: a listbox may only own
  // options and groups, so the search field has to sit outside the list.
  const menuList = (
    <div
      ref={menuRef}
      className={classnames(menu.Menu, menu.MenuLeft, styles.Menu, isSidebar && styles.MenuPortaled)}
      onKeyDown={handleMenuKeyDown}
      style={isSidebar && anchor ? { position: 'fixed', top: anchor.top, left: anchor.left } : undefined}
    >
      {/* An org tree of a few hundred sites can't be navigated by scrolling
          alone, and the Select this replaced was searchable by default. */}
      {sites.sites.length > SEARCH_THRESHOLD && (
        <div className={styles.SearchRow}>
          <input
            ref={searchRef}
            type="text"
            className={styles.Search}
            value={query}
            placeholder={t('profiles.filters.search')}
            aria-label={t('profiles.filters.search')}
            onChange={(e) => setQuery(e.target.value)}
          />
        </div>
      )}
      {tree.length === 0 && unassigned.length === 0 ? (
        <div className={styles.NoResults}>{t('global.noResults')}</div>
      ) : (
        <ul className={styles.List} role="listbox" aria-label={t('devices.table.selectSite')}>
          {tree.map((node) => (
            <OrgBranch key={node.id} node={node} renderSite={renderSite} />
          ))}
          {unassigned.length > 0 && (
            <li role="presentation">
              {/* Only labelled when there's a structure to contrast it with —
                  on a flat account every site would sit under this heading. */}
              {tree.length > 0 && (
                <span className={menu.SectionLabel}>{t('siteSettings.organization.unassigned')}</span>
              )}
              <ul role="group" aria-label={t('siteSettings.organization.unassigned')} className={styles.RootGroup}>
                {unassigned.map(renderSite)}
              </ul>
            </li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <div
      className={classnames(styles.Wrapper, isSidebar && styles.WrapperSidebar, isRail && styles.WrapperRail)}
      ref={wrapperRef}
    >
      <button
        ref={triggerRef}
        type="button"
        className={classnames(styles.Trigger, isSidebar && styles.TriggerSidebar, isRail && styles.TriggerRail)}
        disabled={!enabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t('devices.table.selectSite')}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown' && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        {...railTip.handlers}
      >
        {/* Same mark, size and stroke as the sidebar's site header — the two
            name the same thing and should read as one. */}
        <span className={styles.Marker} aria-hidden="true">
          <Building size={18} strokeWidth={1.85} />
        </span>
        {/* On the rail the mark stands alone: the name it drops is the tooltip,
            and the caret has nowhere to sit beside a centred icon. */}
        {!isRail && (
          <>
            <span className={styles.Labels}>
              {/* Same word in both placements: they're the same control naming
                  the same thing, so a second label would imply a difference. */}
              <span className={classnames(styles.Eyebrow, isSidebar && styles.EyebrowSidebar)}>
                {t('deviceDetail.site')}
              </span>
              <span className={classnames(styles.Name, isSidebar && styles.NameSidebar)}>
                {selectedSite?.name ?? t('devices.table.selectSite')}
              </span>
            </span>
            {enabled && (
              <span className={classnames(styles.Caret, isSidebar && styles.CaretSidebar)} aria-hidden="true">
                ▾
              </span>
            )}
          </>
        )}
      </button>
      {railTip.tooltip}

      {/* In the sidebar the menu is portaled: the nav list is its own scroll
          container, so an absolutely-positioned menu would be clipped by it and
          would scroll away from its trigger. */}
      {open && enabled && (isSidebar ? ReactDOM.createPortal(menuList, document.body) : menuList)}
    </div>
  );
});

interface OrgBranchProps {
  node: SiteOrgNode;
  renderSite: (site: SiteChoice) => React.ReactNode;
}

/** One organization and everything under it. Nested lists rather than computed
 *  indentation, so each level draws its own guide rail and screen readers get
 *  the same grouping sighted users see. */
const OrgBranch: React.FC<OrgBranchProps> = ({ node, renderSite }) => (
  <li role="presentation">
    <span className={styles.OrgName}>{node.name}</span>
    <ul role="group" aria-label={node.name} className={styles.Group}>
      {node.sites.map(renderSite)}
      {node.children.map((child) => (
        <OrgBranch key={child.id} node={child} renderSite={renderSite} />
      ))}
    </ul>
  </li>
);

export default SiteSelector;
